import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { AnalyticsEvent, useAnalytics } from '@bonusly/analytics';
import { useFeatureFlag } from '@bonusly/who-am-i';

import { getHttpClient } from '../../../../lib/http-client';
import { headers } from '../../../../queries/meta';
import { DimensionKey, DraftAssistAPIData, draftAssistQueryKey, loadDraftAssist } from '../../../../queries/recognition-analysis';
import { cleanedDraftLength } from '../utils';

const MIN_CLEANED_DRAFT_LENGTH = 30;

const NOOP = () => {};

/** Maximum times the user can dismiss the same dimension within a draft before generation stops.
 * Resets when the draft is cleared (shorter than the minimum length). */
export const MAX_DISMISSALS_PER_DIMENSION = 3;

/** Priority for first unmet dimension (excludes values_aligned). */
export const DRAFT_ASSIST_DIMENSION_PRIORITY: DimensionKey[] = [DimensionKey.Personal, DimensionKey.Contextual, DimensionKey.Specific, DimensionKey.Genuine];

const CONFIDENCE_UPGRADE_THRESHOLD = 0.5;

export type DraftAssistDimension = {
	key: DimensionKey;
	matches: boolean;
	reason?: string;
};

export type UseDraftAssistProps = {
	dimensions: DraftAssistDimension[];
	draftText: string;
	recipientIds: string[];
	/** When true, scoring is in flight — do not surface a new suggestion yet. */
	isScoring?: boolean;
	/** When false, the LLM call is gated — only fire when the user has opened the panel. */
	isOpen?: boolean;
	/**
	 * When set, generate a suggestion for this specific dimension on-demand (user expanded it).
	 * The fetch starts immediately once scoring completes.
	 * When null, falls back to auto-picking the first unmet dimension once scoring completes.
	 */
	requestedDimensionKey?: DimensionKey | null;
};

export type CurrentDraftSuggestion = {
	text: string;
	dimensionKey: DimensionKey;
};

export type DraftAssistProp = {
	suggestion?: string;
	dimensionKey?: string;
	isLoading?: boolean;
	/** The dimension key currently being fetched (may differ from dimensionKey when no suggestion exists yet). */
	loadingDimensionKey?: string | null;
	onAdd?: (text: string) => void;
	onRegenerate?: () => void;
};

export function pickFirstUnmetDraftAssistDimension(dimensions: DraftAssistDimension[], suppressedDimensions: Set<string>): DimensionKey | null {
	const byKey = new Map(dimensions.map((d) => [d.key, d]));
	for (const key of DRAFT_ASSIST_DIMENSION_PRIORITY) {
		const dim = byKey.get(key);
		if (!dim || dim.matches) continue;
		if (suppressedDimensions.has(key)) continue;
		return key;
	}
	return null;
}

/**
 * Status of the post-action gate:
 * - idle:           ready to fetch
 * - pending_score:  waiting for a fresh score result (after accept or typing with a suggestion visible)
 * - pending_draft:  waiting for a draft change after dismiss
 *
 * Transitions:
 *   accept()              → pending_score
 *   dismiss()             → pending_draft
 *   draft changes         → pending_draft → pending_score
 *   score settles/clears  → pending_score → idle
 */
type SuggestionStatus = 'idle' | 'pending_score' | 'pending_draft';

export function useDraftAssist({ dimensions, draftText, recipientIds, isScoring = false, isOpen = true, requestedDimensionKey = null }: UseDraftAssistProps) {
	const enabled = useFeatureFlag('bizy-draft-assist');
	const { track } = useAnalytics();
	const queryClient = useQueryClient();

	const [status, setStatus] = useState<SuggestionStatus>('idle');
	const [suppressedDimensions, setSuppressedDimensions] = useState<Set<string>>(() => new Set());
	const [draftAssistInteractionIds, setDraftAssistInteractionIds] = useState<string[]>([]);

	/** Per-dimension dismissal counter. Suppresses re-generation after MAX_DISMISSALS_PER_DIMENSION
	 * dismissals within the same draft. Stored as a ref — it never drives rendering directly. */
	const dismissCountsRef = useRef<Map<string, number>>(new Map());

	/**
	 * Tracks which `dimensionKey:suggestion` pairs have already fired SuggestionShown within
	 * the current draft session. Prevents double-counting when cached suggestions are restored
	 * after the user switches between dimensions and back.
	 */
	const seenSuggestionsRef = useRef<Set<string>>(new Set());

	const prevIsScoringRef = useRef(isScoring);
	const prevIsHintVisibleRef = useRef(false);

	/** The dimensionsKey captured when we entered pending_score — cleared when score results arrive. */
	const pendingScoreBaselineRef = useRef<string | null>(null);

	const dimensionsKey = useMemo(
		() =>
			dimensions
				.map((d) => `${d.key}:${d.matches ? '1' : '0'}`)
				.sort()
				.join('|'),
		[dimensions]
	);

	/** Stable string so hasUnmetDimension doesn't recompute on new array literals with same IDs. */
	const recipientIdsKey = useMemo(() => recipientIds.join(','), [recipientIds]);

	// "Adjust state when a prop changes" pattern: react to draft text changes during render
	// rather than in an effect. prevDimensionsKey lags one render behind so we can capture
	// the pre-edit score as baseline even if dimensions happen to update in the same render.
	const [prevDraftText, setPrevDraftText] = useState(draftText);
	const [prevDimensionsKey, setPrevDimensionsKey] = useState(dimensionsKey);

	if (dimensionsKey !== prevDimensionsKey) {
		setPrevDimensionsKey(dimensionsKey);
	}

	if (draftText !== prevDraftText) {
		setPrevDraftText(draftText);
		if (cleanedDraftLength(draftText) < MIN_CLEANED_DRAFT_LENGTH) {
			dismissCountsRef.current = new Map();
			seenSuggestionsRef.current = new Set();
			setSuppressedDimensions(new Set());
		}
		if (status === 'pending_draft') {
			pendingScoreBaselineRef.current = prevDimensionsKey;
			setStatus('pending_score');
		}
	}

	// The dimension we are actively targeting — on-demand when set, auto-pick otherwise.
	const activeTargetKey = requestedDimensionKey ?? pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions);

	const isRequestedDimensionMet = requestedDimensionKey !== null ? (dimensions.find((d) => d.key === requestedDimensionKey)?.matches ?? true) : false;

	const shouldFetch =
		!!enabled &&
		isOpen &&
		!isScoring &&
		status === 'idle' &&
		!!activeTargetKey &&
		recipientIds.length > 0 &&
		cleanedDraftLength(draftText) >= MIN_CLEANED_DRAFT_LENGTH &&
		!isRequestedDimensionMet &&
		(dismissCountsRef.current.get(activeTargetKey) ?? 0) < MAX_DISMISSALS_PER_DIMENSION;

	const { data: queryData, isFetching } = useQuery({
		queryKey: draftAssistQueryKey({ draftText, dimensionKey: activeTargetKey ?? '', recipientIds }),
		queryFn: () => loadDraftAssist({ draftText, dimensionKey: activeTargetKey ?? '', recipientIds }),
		enabled: shouldFetch,
		staleTime: Infinity,
		retry: false,
		select: (data: DraftAssistAPIData) => (data.confidence >= CONFIDENCE_UPGRADE_THRESHOLD ? data : null),
	});

	// A suggestion is valid only when the backend's resolved dimension matches what we are actively serving.
	// This handles backend rerouting: if the backend targets a different dimension, we discard the result.
	const currentSuggestion: CurrentDraftSuggestion | null =
		queryData && queryData.targetDimension === activeTargetKey ? { text: queryData.suggestion, dimensionKey: queryData.targetDimension } : null;

	/** Status machine: pending_score → idle when a fresh score arrives, scoring finishes, or draft clears. */
	useEffect(() => {
		if (status !== 'pending_score') {
			prevIsScoringRef.current = isScoring;
			return;
		}

		const scoringJustCompleted = prevIsScoringRef.current && !isScoring;
		const dimensionsUpdated = pendingScoreBaselineRef.current !== null && dimensionsKey !== pendingScoreBaselineRef.current;
		const draftTooShort = cleanedDraftLength(draftText) < MIN_CLEANED_DRAFT_LENGTH;

		if (scoringJustCompleted || dimensionsUpdated || draftTooShort) {
			setStatus('idle');
		}

		prevIsScoringRef.current = isScoring;
	}, [isScoring, dimensionsKey, draftText, status]);

	/** Track analytics when a new suggestion first surfaces within the current draft session. */
	useEffect(() => {
		if (queryData) {
			const key = `${queryData.targetDimension}:${queryData.suggestion}`;
			if (!seenSuggestionsRef.current.has(key)) {
				seenSuggestionsRef.current.add(key);
				track(AnalyticsEvent.DraftAssist.SuggestionShown, {
					dimensionKey: queryData.targetDimension,
				});
			}
		}
	}, [queryData, track]);

	// Signal that Bizy can help once we are idle, scoring has finished, and the draft is long enough.
	// recipientIdsKey guard mirrors shouldFetch — no recipients means no suggestion can be fetched.
	const hasUnmetDimension = useMemo(
		() =>
			!!enabled &&
			!isScoring &&
			status === 'idle' &&
			recipientIdsKey.length > 0 &&
			cleanedDraftLength(draftText) >= MIN_CLEANED_DRAFT_LENGTH &&
			pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions) !== null,
		[enabled, isScoring, status, recipientIdsKey, draftText, dimensions, suppressedDimensions]
	);

	/** The unmet dimension the hint bubble refers to — used to include dimensionKey in the hint-clicked event. */
	const hintDimensionKey = useMemo(
		() => (hasUnmetDimension ? pickFirstUnmetDraftAssistDimension(dimensions, suppressedDimensions) : null),
		[hasUnmetDimension, dimensions, suppressedDimensions]
	);

	/** Track when the hint bubble first becomes visible (false → true transition). */
	useEffect(() => {
		const isHintVisible = hasUnmetDimension && !isOpen;
		const wasHintVisible = prevIsHintVisibleRef.current;
		prevIsHintVisibleRef.current = isHintVisible;
		if (isHintVisible && !wasHintVisible && hintDimensionKey) {
			track(AnalyticsEvent.GratitudeGauge.DraftAssistHintShown, { dimensionKey: hintDimensionKey });
		}
	}, [hasUnmetDimension, isOpen, hintDimensionKey, track]);

	const accept = useCallback(async () => {
		if (!queryData || queryData.targetDimension !== activeTargetKey) return;
		const data = queryData;
		queryClient.removeQueries({ queryKey: draftAssistQueryKey({ draftText, dimensionKey: data.targetDimension, recipientIds }) });
		pendingScoreBaselineRef.current = dimensionsKey;
		setStatus('pending_score');

		let interactionId: string | undefined;
		try {
			const http = await getHttpClient();
			const response = await http.post<{ id: string }>(
				'/bizy/recognition/draft_assist_interactions',
				{
					draftBeforeAccept: draftText,
					suggestionText: data.suggestion,
					dimension: data.targetDimension,
					confidence: data.confidence,
					accepted: true,
					recipientIds,
				},
				{ headers }
			);
			interactionId = response.data.id;
			setDraftAssistInteractionIds((prev) => [...prev, interactionId as string]);
		} catch {
			/* Non-critical */
		}

		track(AnalyticsEvent.DraftAssist.SuggestionAccepted, {
			...(interactionId ? { draftId: interactionId } : {}),
			dimensionKey: data.targetDimension,
		});
	}, [queryData, activeTargetKey, queryClient, draftText, recipientIds, track, dimensionsKey]);

	/**
	 * User clicked "Re-generate" — they want a fresh suggestion for the same dimension.
	 * For the first (MAX_DISMISSALS_PER_DIMENSION - 1) clicks: clear the cache and stay idle
	 * so React Query re-fetches a new suggestion immediately.
	 * Once the limit is reached: suppress the dimension and enter the pending_draft gate,
	 * requiring a draft edit before suggestions resume (same as if the user had dismissed entirely).
	 */
	const regenerate = useCallback(() => {
		if (!queryData || queryData.targetDimension !== activeTargetKey) return;
		const key = queryData.targetDimension;
		const data = queryData;
		track(AnalyticsEvent.DraftAssist.SuggestionDismissed, { dimensionKey: key });
		const newCount = (dismissCountsRef.current.get(key) ?? 0) + 1;
		dismissCountsRef.current = new Map(dismissCountsRef.current).set(key, newCount);
		if (newCount >= MAX_DISMISSALS_PER_DIMENSION) {
			// Final dismiss: remove cache without re-fetching, then suppress.
			queryClient.removeQueries({ queryKey: draftAssistQueryKey({ draftText, dimensionKey: key, recipientIds }) });
			setSuppressedDimensions((prev) => new Set(prev).add(key));
			setStatus('pending_draft');
		} else {
			// Below the cap: clear cache so React Query re-fetches a new suggestion immediately.
			queryClient.resetQueries({ queryKey: draftAssistQueryKey({ draftText, dimensionKey: key, recipientIds }) });
		}

		getHttpClient()
			.then((http) =>
				http.post<{ id: string }>(
					'/bizy/recognition/draft_assist_interactions',
					{
						draftBeforeAccept: draftText,
						suggestionText: data.suggestion,
						dimension: data.targetDimension,
						confidence: data.confidence,
						accepted: false,
						recipientIds,
					},
					{ headers }
				)
			)
			.then((response) => {
				setDraftAssistInteractionIds((prev) => [...prev, response.data.id]);
			})
			.catch(() => {
				/* Non-critical */
			});
	}, [queryData, activeTargetKey, queryClient, draftText, recipientIds, track]);

	if (!enabled) {
		return {
			suggestion: null,
			isLoading: false,
			dimensionKey: null,
			hintDimensionKey: null,
			hasUnmetDimension: false,
			draftAssistInteractionIds: [] as string[],
			accept: NOOP,
			regenerate: NOOP,
		};
	}

	return {
		suggestion: currentSuggestion?.text ?? null,
		isLoading: isFetching,
		// While re-fetching, keep dimensionKey pointing at the active target so the accordion
		// doesn't collapse (overrideIndex would drop to null if we returned null here).
		dimensionKey: currentSuggestion?.dimensionKey ?? (isFetching && activeTargetKey ? activeTargetKey : null),
		hintDimensionKey,
		hasUnmetDimension,
		draftAssistInteractionIds,
		accept,
		regenerate,
	};
}
